import { isGridSpecialColumn, refreshVisibleColumns } from '../column/meta'
import { applyHeaderMinWidths } from '../column/resolve-header-min-width'
import { resolveSideBarOptions } from '../utils/side-bar'
import type { DataGrid, GridColumnInfo, GridFixed, GridCustomColumnDraft } from '../types'

export type { GridCustomColumnDraft }

/** 列设置面板中不展示的列（序号、行选复选框等系统列） */
export function isCustomColumnExcludedFromPanel(column: Pick<GridColumnInfo, 'type'>) {
  return isGridSpecialColumn(column)
}

function cloneColumnDraft(col: GridColumnInfo): GridCustomColumnDraft {
  return {
    ...col,
    checked: col.visible !== false,
    fixed: col.fixed ?? '',
  }
}

export function cloneColumnDraftList(columns: GridColumnInfo[]): GridCustomColumnDraft[] {
  return columns.filter((col) => !isCustomColumnExcludedFromPanel(col)).map(cloneColumnDraft)
}

function reorderColumnsWithExcluded(
  allColumns: GridColumnInfo[],
  draft: GridCustomColumnDraft[]
): GridColumnInfo[] {
  const idToColumn = new Map(allColumns.map((col) => [col.id, col]))
  const draftQueue = draft
    .map((item) => idToColumn.get(item.id))
    .filter((col): col is GridColumnInfo => col != null)
  let draftIndex = 0
  const reordered: GridColumnInfo[] = []

  for (const col of allColumns) {
    if (isCustomColumnExcludedFromPanel(col)) {
      reordered.push(col)
      continue
    }
    const next = draftQueue[draftIndex]
    if (next) {
      reordered.push(next)
      draftIndex += 1
    }
  }

  while (draftIndex < draftQueue.length) {
    reordered.push(draftQueue[draftIndex]!)
    draftIndex += 1
  }

  return reordered
}

export function cloneColumnBaseline(columns: GridColumnInfo[]): GridColumnInfo[] {
  return columns.map((col) => ({
    ...col,
    fixed: col.fixed ?? '',
  }))
}

function syncCustomColumnsDraft($grid: DataGrid) {
  $grid.reactData.customStore.columns = cloneColumnDraftList($grid.internalData.allColumns)
}

function applyCustomColumnsInternal($grid: DataGrid) {
  const { reactData, internalData } = $grid
  const draft = reactData.customStore.columns
  if (!draft.length) {
    return
  }

  const hiddenFields = new Set(
    draft.filter((col) => !col.checked && col.field).map((col) => col.field!)
  )
  const idToColumn = new Map(internalData.allColumns.map((col) => [col.id, col]))

  for (const draftCol of draft) {
    const col = idToColumn.get(draftCol.id)
    if (!col) {
      continue
    }
    if (col.field) {
      col.visible = !hiddenFields.has(col.field)
    } else {
      col.visible = true
    }
    col.fixed = draftCol.fixed ?? ''
    if (draftCol.width != null && draftCol.width > 0) {
      col.width = draftCol.width
    }
  }

  internalData.allColumns = reorderColumnsWithExcluded(internalData.allColumns, draft)
  applyHeaderMinWidths(internalData.allColumns, $grid.options, $grid.props, $grid.gridSize?.value)
  refreshVisibleColumns($grid)
}

function restoreColumnsFromSnapshot($grid: DataGrid, snapshot: GridColumnInfo[]) {
  $grid.internalData.allColumns = cloneColumnBaseline(snapshot)
  refreshVisibleColumns($grid)
}

export function captureColumnBaseline($grid: DataGrid) {
  $grid.internalData.baselineColumns = cloneColumnBaseline($grid.internalData.allColumns)
}

export function useCustom($grid: DataGrid) {
  const { reactData, internalData, options } = $grid

  function getSideBar() {
    return resolveSideBarOptions(options)
  }

  function openColumnsPanel(highlightColumnId?: string | null) {
    syncCustomColumnsDraft($grid)
    reactData.customStore.snapshot = cloneColumnBaseline(internalData.allColumns)
    reactData.customStore.open = true
    reactData.customStore.highlightColumnId = highlightColumnId ?? null
    reactData.customStore.keyword = ''
    reactData.initStore.custom = true
  }

  $grid.applyCustomColumns = () => {
    applyCustomColumnsInternal($grid)
  }

  $grid.openCustom = (highlightColumnId?: string | null) => {
    openColumnsPanel(highlightColumnId)
  }

  $grid.toggleColumnsPanel = () => {
    if (reactData.customStore.open) {
      $grid.closeCustom?.()
      return
    }
    openColumnsPanel()
  }

  $grid.closeCustom = () => {
    if (!getSideBar().liveUpdate && reactData.customStore.snapshot.length) {
      restoreColumnsFromSnapshot($grid, reactData.customStore.snapshot)
      syncCustomColumnsDraft($grid)
    }
    reactData.customStore.open = false
    reactData.customStore.highlightColumnId = null
    reactData.customStore.snapshot = []
  }

  $grid.cancelCustom = () => {
    $grid.closeCustom?.()
  }

  $grid.confirmCustom = () => {
    applyCustomColumnsInternal($grid)
    reactData.customStore.snapshot = cloneColumnBaseline(internalData.allColumns)
    reactData.customStore.open = false
    reactData.customStore.highlightColumnId = null
  }

  $grid.resetCustom = () => {
    const baseline = internalData.baselineColumns
    if (baseline.length) {
      internalData.allColumns = cloneColumnBaseline(baseline)
    } else {
      internalData.allColumns.forEach((col) => {
        col.visible = true
        col.fixed = ''
      })
    }
    syncCustomColumnsDraft($grid)
    refreshVisibleColumns($grid)
    reactData.customStore.snapshot = cloneColumnBaseline(internalData.allColumns)
  }

  if (getSideBar().defaultOpen) {
    openColumnsPanel()
  }
}

export function isCustomColumnToggleDisabled(column: GridColumnInfo) {
  return isGridSpecialColumn(column) || !column.field
}

export function isCustomColumnPinDisabled(column: GridColumnInfo) {
  return isGridSpecialColumn(column) || !column.field
}

export function cycleColumnFixed(fixed?: GridFixed): GridFixed {
  if (fixed === 'left') {
    return 'right'
  }
  if (fixed === 'right') {
    return ''
  }
  return 'left'
}

export function countFixedColumns(
  columns: GridCustomColumnDraft[],
  side?: 'left' | 'right'
): number {
  const fixedCols = columns.filter(
    (col) =>
      col.checked !== false && (col.fixed === 'left' || col.fixed === 'right')
  )
  if (side === 'left') {
    return fixedCols.filter((col) => col.fixed === 'left').length
  }
  if (side === 'right') {
    return fixedCols.filter((col) => col.fixed === 'right').length
  }
  return fixedCols.length
}

export function setColumnFixed(
  col: GridCustomColumnDraft,
  fixed: GridFixed,
  columns: GridCustomColumnDraft[],
  maxFixedCount: number
): boolean {
  const nextFixed = fixed ?? ''
  if (col.fixed === nextFixed) {
    return true
  }
  const isCurrentlyFixed = col.fixed === 'left' || col.fixed === 'right'
  const willBeFixed = nextFixed === 'left' || nextFixed === 'right'
  if (!isCurrentlyFixed && willBeFixed && countFixedColumns(columns) >= maxFixedCount) {
    return false
  }
  col.fixed = nextFixed
  return true
}

export function shouldApplyCustomLive($grid: DataGrid) {
  return resolveSideBarOptions($grid.options).liveUpdate
}
